/**
 * PRODUTOS — o portfólio.
 *
 * Esta tela existe por um motivo bem concreto: o passo 1 do assistente de
 * campanha EXIGE um produto, e sem ele o botão "Continuar" nunca habilita.
 * A API já aceitava o cadastro desde o início; o que faltava era um lugar
 * para fazê-lo.
 *
 * E o produto não é rótulo decorativo. Ele é a chave da regra 4 de
 * `compliance.pode_contatar`: "este contato já ouviu ESTA oferta nos últimos
 * N dias". Dois produtos com o mesmo nome, ou uma campanha de banda larga
 * cadastrada como "outros", fazem essa regra deixar de casar — e o contato
 * recebe a mesma oferta de novo, que é o comportamento que queima número.
 *
 * Por isso a tela insiste em duas coisas: nome distinto (o servidor recusa
 * duplicata) e desativar em vez de apagar.
 */
import { useCallback, useMemo, useState } from "react";
import { api } from "../api.js";
import { irPara } from "../Casca.js";
import {
  Aviso,
  Botao,
  Campo,
  Indicador,
  Painel,
  Selo,
  Tabela,
  classeEntrada,
} from "../ui/base.js";
import {
  Conteudo,
  Gaveta,
  Indicadores,
  LinhaVazia,
  Td,
  fmtDataHora,
  useCarga,
} from "../ui/comercial.js";

interface Produto {
  id: string;
  nome: string;
  familia: string | null;
  ativo: boolean;
  criadoEm: string;
  campanhasUsando: number;
}

/**
 * Famílias sugeridas, não impostas. São as do catálogo Vivo PME que já
 * aparecem no comentário da migration 012 — mas o campo é texto livre porque
 * o próximo cliente incubado vende outra coisa, e enum aqui viraria migration
 * a cada produto novo.
 */
const FAMILIAS = [
  "conectividade",
  "mobilidade",
  "seguranca",
  "licenciamento",
  "hardware",
];

export function Produtos(): JSX.Element {
  const { estado, recarregar } = useCarga(
    useCallback(() => api.produtos() as Promise<{ itens: Produto[] }>, []),
  );
  const [editando, setEditando] = useState<Produto | "novo" | null>(null);
  const [recado, setRecado] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);

  async function alternar(p: Produto): Promise<void> {
    setOcupado(p.id);
    setRecado(null);
    try {
      const r = await api.editarProduto(p.id, { ativo: !p.ativo });
      setRecado(r.motivo ?? null);
      recarregar();
    } catch (e: unknown) {
      setRecado(e instanceof Error ? e.message : "O servidor recusou a mudança.");
    } finally {
      setOcupado(null);
    }
  }

  return (
    <div className="grid gap-4">
      <Conteudo estado={estado} aoRecarregar={recarregar}>
        {(d) => {
          const ativos = d.itens.filter((p) => p.ativo);
          const semUso = d.itens.filter((p) => p.campanhasUsando === 0);
          const familias = new Set(
            d.itens.map((p) => p.familia).filter((f): f is string => Boolean(f)),
          );
          return (
            <div className="grid gap-4">
              <Indicadores>
                <Indicador
                  rotulo="Produtos"
                  valor={d.itens.length}
                  nota={`${ativos.length} disponível(is) para campanha nova`}
                />
                <Indicador
                  rotulo="Nunca usados"
                  valor={semUso.length}
                  nota="Cadastrados mas sem nenhuma campanha"
                />
                <Indicador
                  rotulo="Famílias"
                  valor={familias.size}
                  nota="Agrupam o portfólio; não mudam regra"
                />
              </Indicadores>

              {recado && <Aviso tom="warn">{recado}</Aviso>}

              {ativos.length === 0 && (
                <Aviso tom="crit">
                  Nenhum produto ativo. Enquanto for assim,{" "}
                  <strong>nenhuma campanha pode ser criada</strong> — o passo 1
                  do assistente exige escolher um produto e não deixa seguir sem.
                </Aviso>
              )}

              <Painel
                titulo="Portfólio"
                dica="O que você vende. É por aqui que a campanha sabe o que está ofertando."
                acoes={
                  <Botao tipo="principal" pequeno onClick={() => setEditando("novo")}>
                    Novo produto
                  </Botao>
                }
                semCorpo
              >
                <Tabela
                  colunas={[
                    { chave: "nome", rotulo: "Produto" },
                    { chave: "familia", rotulo: "Família" },
                    { chave: "estado", rotulo: "Estado" },
                    { chave: "campanhas", rotulo: "Campanhas", direita: true },
                    { chave: "criado", rotulo: "Cadastrado" },
                    { chave: "acoes", rotulo: "", direita: true },
                  ]}
                >
                  {d.itens.length === 0 ? (
                    <LinhaVazia colunas={6}>
                      Nenhum produto cadastrado ainda. Comece pelo que você mais
                      vende — o nome que aparecer aqui é o que separa uma oferta
                      da outra na regra de descanso.
                    </LinhaVazia>
                  ) : (
                    d.itens.map((p) => (
                      <tr key={p.id} className="border-t border-linha">
                        <Td>
                          <strong className="text-[13px]">{p.nome}</strong>
                        </Td>
                        <Td className="text-[12.5px] text-tinta-2">
                          {p.familia ?? "—"}
                        </Td>
                        <Td>
                          <Selo tom={p.ativo ? "ok" : "idle"}>
                            {p.ativo ? "ativo" : "desativado"}
                          </Selo>
                        </Td>
                        <Td direita className="text-[12.5px] text-tinta-2">
                          {p.campanhasUsando}
                        </Td>
                        <Td className="text-[11.5px] text-tinta-3 whitespace-nowrap">
                          {fmtDataHora(p.criadoEm)}
                        </Td>
                        <Td direita>
                          <div className="flex gap-1.5 justify-end flex-wrap">
                            <Botao
                              pequeno
                              tipo="fantasma"
                              onClick={() => setEditando(p)}
                            >
                              Editar
                            </Botao>
                            <Botao
                              pequeno
                              tipo="fantasma"
                              desabilitado={ocupado === p.id}
                              titulo={
                                p.ativo
                                  ? "Some do cadastro de campanha nova; o que já rodou continua contando"
                                  : "Volta a aparecer ao criar campanha"
                              }
                              onClick={() => void alternar(p)}
                            >
                              {p.ativo ? "Desativar" : "Reativar"}
                            </Botao>
                          </div>
                        </Td>
                      </tr>
                    ))
                  )}
                </Tabela>
              </Painel>

              <Painel
                titulo="Por que não existe excluir"
                dica="A pergunta aparece sempre; a resposta está no banco"
              >
                <p className="text-[13px] text-tinta-2 leading-relaxed">
                  Cada contato tocado guarda qual produto foi ofertado a ele. Se
                  o produto fosse apagado, essa memória viraria nulo — e a regra
                  &ldquo;mesma oferta, mesmo contato&rdquo; pararia de casar em
                  silêncio. Na prática, a base voltaria a ficar elegível para a{" "}
                  <strong>mesma oferta</strong> no dia seguinte, sem nenhum aviso
                  na tela. Desativar resolve o que excluir resolveria: o produto
                  some do cadastro de campanha nova, e tudo o que já rodou
                  continua contando para o descanso.
                </p>
              </Painel>

              {ativos.length > 0 && (
                <div>
                  <Botao onClick={() => irPara("campanhas")}>
                    Ir para campanhas
                  </Botao>
                </div>
              )}
            </div>
          );
        }}
      </Conteudo>

      {editando && (
        <FormularioProduto
          alvo={editando === "novo" ? null : editando}
          aoFechar={() => setEditando(null)}
          aoSalvar={(msg) => {
            setEditando(null);
            setRecado(msg);
            recarregar();
          }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Formulário
   ══════════════════════════════════════════════════════════════════════ */

function FormularioProduto({
  alvo,
  aoFechar,
  aoSalvar,
}: {
  alvo: Produto | null;
  aoFechar: () => void;
  aoSalvar: (motivo: string | null) => void;
}): JSX.Element {
  const [nome, setNome] = useState(alvo?.nome ?? "");
  const [familia, setFamilia] = useState(alvo?.familia ?? "");
  const [ativo, setAtivo] = useState(alvo?.ativo ?? true);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  // O veredito aparece enquanto se digita, não só ao clicar salvar.
  const problema = useMemo(() => {
    const n = nome.trim();
    if (n.length < 2) return "O nome precisa de pelo menos 2 caracteres.";
    if (n.length > 120) return "O nome passou de 120 caracteres.";
    return null;
  }, [nome]);

  async function salvar(): Promise<void> {
    if (problema) {
      setErro(problema);
      return;
    }
    setOcupado(true);
    setErro(null);
    try {
      const corpo = { nome: nome.trim(), familia: familia.trim(), ativo };
      const r = alvo
        ? await api.editarProduto(alvo.id, corpo)
        : await api.criarProduto(corpo);
      aoSalvar(r.motivo ?? null);
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : "O servidor recusou o cadastro.");
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Gaveta
      titulo={alvo ? `Editar ${alvo.nome}` : "Novo produto"}
      dica="O nome separa uma oferta da outra na regra de descanso"
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao
            tipo="principal"
            desabilitado={ocupado || problema !== null}
            onClick={() => void salvar()}
          >
            {ocupado ? "Salvando…" : "Salvar"}
          </Botao>
          <Botao tipo="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          {problema && (
            <span className="text-[12px] text-tinta-3 ml-auto max-w-[40ch] leading-relaxed">
              {problema}
            </span>
          )}
        </>
      }
    >
      <div className="grid gap-4">
        {erro && <Aviso tom="crit">{erro}</Aviso>}

        <Campo rotulo="Nome">
          <input
            className={classeEntrada}
            value={nome}
            placeholder="Banda larga 500 Mega"
            onChange={(e) => setNome(e.target.value)}
          />
        </Campo>

        <Aviso tom="warn">
          Nome específico, não genérico. Banda larga e link dedicado são ofertas
          diferentes e precisam de produtos diferentes — se as duas entrarem
          como &ldquo;internet&rdquo;, o contato que ouviu uma na segunda recebe
          a outra na quinta, e o descanso não vai barrar.
        </Aviso>

        <Campo rotulo="Família (opcional)">
          <input
            className={classeEntrada}
            value={familia}
            list="familias-sugeridas"
            placeholder="conectividade"
            onChange={(e) => setFamilia(e.target.value)}
          />
          <datalist id="familias-sugeridas">
            {FAMILIAS.map((f) => (
              <option key={f} value={f} />
            ))}
          </datalist>
        </Campo>
        <p className="text-[12px] text-tinta-3 leading-relaxed -mt-2">
          Só agrupa o portfólio na leitura. Não muda nenhuma regra de envio nem
          de descanso — quem faz isso é o nome.
        </p>

        {alvo && (
          <Campo rotulo="Disponível para campanha nova">
            <label className="flex items-center gap-2 text-[13px] text-tinta-2">
              <input
                type="checkbox"
                checked={ativo}
                onChange={(e) => setAtivo(e.target.checked)}
              />
              {ativo
                ? "Sim, aparece no assistente"
                : "Não, fica fora do assistente"}
            </label>
          </Campo>
        )}

        {alvo && alvo.campanhasUsando > 0 && (
          <Aviso tom="warn">
            {alvo.campanhasUsando} campanha(s) já usam este produto. Renomear
            muda o rótulo em todas elas — o histórico de descanso continua
            intacto, porque ele segue o identificador, não o nome.
          </Aviso>
        )}
      </div>
    </Gaveta>
  );
}
